import { useEffect } from 'react'
import { Button, Skeleton } from '@heroui/react'
import { useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { FormModal } from '@/components/ui/form-modal'
import {
  NumberFieldControl,
  SelectFieldControl,
  SwitchFieldControl,
  TextFieldControl,
} from '@/components/ui/form-fields'
import { PlusIcon, TrashIcon } from '@/components/ui/icons'
import { toTranslation } from '@/lib/api/common'

import { useChoiceGroup, useChoiceGroupActions } from '../hooks'
import { ChoiceType } from '../types'

const schema = z.object({
  nameEn: z.string().trim().min(1, 'Give the group a name.'),
  nameKm: z.string().trim().optional(),
  selectType: z.enum([ChoiceType.Single, ChoiceType.Multiple]),
  isRequire: z.boolean(),
  items: z
    .array(
      z.object({
        id: z.string().optional(),
        nameEn: z.string().trim().min(1, 'Name this option.'),
        nameKm: z.string().trim().optional(),
        price: z.number({ message: 'Price is required.' }).min(0, 'Price cannot be negative.'),
      }),
    )
    .min(1, 'A group needs at least one option.'),
})

type FormValues = z.infer<typeof schema>

const emptyForm: FormValues = {
  nameEn: '',
  nameKm: '',
  selectType: ChoiceType.Single,
  isRequire: false,
  items: [{ nameEn: '', nameKm: '', price: 0 }],
}

interface ChoiceGroupFormDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  /** The group being edited, or `null` for a new one. */
  groupId: string | null
  onSaved: () => void
}

/**
 * A choice group and its options in one dialog.
 *
 * The group is loaded from the server rather than read off the clicked row: the
 * list carries no items, and saving from a partial picture would delete every
 * option the group has.
 */
export function ChoiceGroupFormDialog({
  isOpen,
  onOpenChange,
  groupId,
  onSaved,
}: ChoiceGroupFormDialogProps) {
  const { group, isLoading } = useChoiceGroup(isOpen ? groupId : null)
  const { save } = useChoiceGroupActions()

  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyForm,
    mode: 'onTouched',
  })

  const items = useFieldArray({ control, name: 'items' })

  useEffect(() => {
    if (!isOpen) return

    if (!groupId) {
      reset(emptyForm)
      return
    }

    if (!group) return

    reset({
      nameEn: group.name?.en ?? '',
      nameKm: group.name?.km ?? '',
      selectType: (group.selectType as ChoiceType) ?? ChoiceType.Single,
      isRequire: group.isRequire ?? false,
      items: group.items.length
        ? group.items.map((item) => ({
            id: item.id,
            nameEn: item.name?.en ?? '',
            nameKm: item.name?.km ?? '',
            price: Number(item.price ?? 0),
          }))
        : emptyForm.items,
    })
  }, [isOpen, groupId, group, reset])

  const submit = handleSubmit(async (values) => {
    await save.mutateAsync({
      id: groupId,
      group: {
        name: toTranslation(values.nameEn, values.nameKm),
        selectType: values.selectType,
        isRequire: values.isRequire,
      },
      items: values.items.map((item) => ({
        id: item.id,
        name: toTranslation(item.nameEn, item.nameKm),
        price: item.price,
      })),
      originalItems: group?.items ?? [],
    })

    onSaved()
  })

  const isBusy = save.isPending || formState.isSubmitting

  return (
    <FormModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={groupId ? 'Edit choice group' : 'New choice group'}
      description="Add-ons a customer picks while ordering — sizes, sauces, extras."
      submitLabel={groupId ? 'Save changes' : 'Create group'}
      onSubmit={submit}
      isBusy={isBusy}
      error={save.error}
      errorFallback="Could not save this choice group."
    >
      {isLoading ? (
        <Skeleton className="h-64 w-full rounded-xl" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextFieldControl
              control={control}
              name="nameEn"
              label="Name (English)"
              isRequired
              autoFocus
            />
            <TextFieldControl control={control} name="nameKm" label="Name (Khmer)" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectFieldControl
              control={control}
              name="selectType"
              label="Selection"
              options={[
                { value: ChoiceType.Single, label: 'Pick one' },
                { value: ChoiceType.Multiple, label: 'Pick several' },
              ]}
            />
            <SwitchFieldControl
              control={control}
              name="isRequire"
              label="Required"
              description="The customer cannot add the product without choosing."
            />
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Options</h3>
              <Button
                variant="ghost"
                size="sm"
                className="border-border h-8 rounded-full border px-3 text-xs"
                onPress={() => items.append({ nameEn: '', nameKm: '', price: 0 })}
              >
                <PlusIcon width={14} height={14} />
                Add option
              </Button>
            </div>

            <div className="flex flex-col gap-3">
              {items.fields.map((field, index) => (
                <div
                  key={field.id}
                  className="border-border grid gap-3 rounded-xl border p-3 sm:grid-cols-[1fr_1fr_140px_auto]"
                >
                  <TextFieldControl
                    control={control}
                    name={`items.${index}.nameEn`}
                    label="Option (English)"
                    isRequired
                  />
                  <TextFieldControl
                    control={control}
                    name={`items.${index}.nameKm`}
                    label="Option (Khmer)"
                  />
                  <NumberFieldControl
                    control={control}
                    name={`items.${index}.price`}
                    label="Extra"
                    minValue={0}
                    step={0.01}
                    formatOptions={{ style: 'currency', currency: 'USD' }}
                  />

                  <Button
                    variant="ghost"
                    aria-label="Remove option"
                    // A group with no options cannot be saved, so the last row keeps
                    // a disabled remove button rather than losing it — the control
                    // does not jump around as rows are added and taken away.
                    isDisabled={items.fields.length === 1}
                    onPress={() => items.remove(index)}
                    className="text-muted hover:text-danger size-10 self-end rounded-full"
                  >
                    <TrashIcon width={16} height={16} />
                  </Button>
                </div>
              ))}
            </div>

            {formState.errors.items?.message && (
              <p role="alert" className="text-danger text-xs">
                {formState.errors.items.message}
              </p>
            )}
          </div>
        </>
      )}
    </FormModal>
  )
}
